import { Alert, Form, Modal, notification, Space } from 'antd'
import type { FormInstance } from 'antd/es/form'
import { FC, useMemo } from 'react'
import MaterialRequirements from '../../../../../../calculations/components/shipment-creation-form/components/material-requirements/material-requirements'
import { DataSourceItem } from '../../../../../shipments-page'
import { MaterialRequirements as MaterialRequirementsValue } from '../../../../../../calculations/components/shipment-creation-form/types/form-values'
import { useErrorModal } from '../../../../../../../hooks/api/use-error-modal'
import { useMaterialRequirementsHandled } from './hooks/use-material-requirements-handled'
import { StrapiUpdate } from '../../../../../../../types/strapi/strapi'
import Shipment from '../../../../../../../types/api/shipment'
import {
    useLaunchPendingMaterialsMutation,
    useUpdateAndCalculateShipmentMutation,
} from '../../../../../../../store/api/shipments-api'
import useEndpointQueryParams from '../../../../../../../hooks/table-pages/use-endpoint-query-parameters'
import { deserializeFilters } from '../../../../../helpers/deserialize-filters'
import { defaultParams } from '../../../../../constants/default-params'
import { useUpdateQueryData } from '../../../../../hooks/use-update-query-data'
import { getShipmentDisplayCalculated } from '../../../../../../../helpers/entities/shipment/get-shipment-display-calculated'
import { getMinSelectableMaterialWidth } from '../../../../../../../helpers/material-width'

type UpdateShipment = StrapiUpdate<Shipment>
type UpdateShipmentMaterialRequirement = Exclude<
    UpdateShipment['body']['data']['materialRequirements'],
    undefined
>[number]
type UpdateShipmentMaterialRequirementsConfiguration = Exclude<
    UpdateShipmentMaterialRequirement['configurations'],
    undefined
>[number]

interface MaterialRequirementsWrapperProps {
    record: DataSourceItem
    form: FormInstance
    open: boolean
    onClose: () => void
}

const MaterialRequirementsWrapper: FC<MaterialRequirementsWrapperProps> = ({
    record,
    form,
    open,
    onClose,
}) => {
    const materialRequirementsHandled = useMaterialRequirementsHandled(record)
    const [updateShipment, updateShipmentState] = useUpdateAndCalculateShipmentMutation()
    const [launchPendingMaterials, launchPendingMaterialsState] =
        useLaunchPendingMaterialsMutation()
    const displayCalculated = getShipmentDisplayCalculated(record)
    const necessary = displayCalculated?.materialLength
    const params = useEndpointQueryParams({ deserializeFilters })
    const queryArg = { ...params, ...defaultParams }
    const updateQueryData = useUpdateQueryData(queryArg)

    const okButtonLoading = updateShipmentState.isLoading || launchPendingMaterialsState.isLoading

    // У shipment нет additional_material_types: основной MR всегда первый (как в use-repeat-order).
    const mainMaterialTypeDocId = record.materialRequirements?.[0]?.material_type?.documentId

    const mainOrientirWidth =
        record.materialRequirements?.[0]?.configurations?.[0]?.material?.width ??
        displayCalculated?.materialWidth

    const minMainMaterialWidth = useMemo(
        () => getMinSelectableMaterialWidth(mainOrientirWidth),
        [mainOrientirWidth]
    )

    useErrorModal(launchPendingMaterialsState, 'Ошибка запуска обработки незапущенных материалов')

    const getConfigurations = (
        requirements: MaterialRequirementsValue
    ): UpdateShipmentMaterialRequirementsConfiguration[] => {
        const list = requirements.configurations
        const payloadList: UpdateShipmentMaterialRequirementsConfiguration[] = []

        for (const config of list) {
            if (!config.material || typeof config.material !== 'string') {
                Modal.error({ title: 'Выберите ширину из списка' })
                return []
            }

            payloadList.push({
                // @ts-ignore
                id: config.componentId,
                amount: config.amount,
                action: config.action,
                material: { connect: [config.material] },
            })
        }

        return payloadList
    }

    const extractMaterialRequirement = (
        mr: MaterialRequirementsValue
    ): UpdateShipmentMaterialRequirement => {
        return {
            // @ts-ignore TODO: разобраться почему ставим ид компонента
            id: mr.componentId,
            material_type: { connect: [mr.materialTypeDocId] },
            configurations: getConfigurations(mr),
        }
    }

    const extractMaterialRequirements = (list: MaterialRequirementsValue[]) => {
        return list.map(item => extractMaterialRequirement(item))
    }

    const onSave = async () => {
        try {
            await form.validateFields()
            const values = form.getFieldsValue().materialRequirements as MaterialRequirementsValue[]
            const requirements = extractMaterialRequirements(values)
            const res = await updateShipment({
                docId: record.documentId || '',
                body: {
                    data: {
                        materialRequirements: requirements,
                    },
                },
                queryParams: queryArg,
            })

            if (materialRequirementsHandled) {
                const launchPendingMaterialsRes = await launchPendingMaterials({
                    docId: record.documentId || '',
                    body: { data: {} },
                    queryParams: queryArg,
                })

                if (launchPendingMaterialsRes.data) {
                    updateQueryData(launchPendingMaterialsRes.data)
                    notification.success({ message: 'Незапущенные материалы запущены' })
                    onClose()
                }
            } else {
                if (res.data) {
                    updateQueryData(res.data.data)
                    notification.success({ message: 'Конфигурации материалов сохранены' })
                    onClose()
                }
            }
        } catch (e) {
            console.error(e)
            notification.error({
                message: 'Ошибка обработки сохранения материалов',
                description: e instanceof Error ? e.message : 'Неизвестная ошибка',
            })
        }
    }

    return (
        <Modal
            title={
                materialRequirementsHandled
                    ? 'Материалы уже в работе (резерв/заказ/разрез)'
                    : 'Материалы'
            }
            width={1000}
            open={open}
            onCancel={onClose}
            okText={'Сохранить'}
            onOk={onSave}
            okButtonProps={{
                loading: okButtonLoading,
            }}
            destroyOnClose
        >
            <Form form={form} colon={false} layout={'vertical'} style={{ width: '100%' }}>
                <Space style={{ width: '100%' }} direction={'vertical'} size={12}>
                    <Form.List name={['materialRequirements']}>
                        {fields => (
                            <Space style={{ width: '100%' }} direction={'vertical'} size={12}>
                                {fields.map(field => {
                                    const materialTypeDocId = form.getFieldValue([
                                        'materialRequirements',
                                        field.name,
                                        'materialTypeDocId',
                                    ])
                                    const isAdditionalMaterial =
                                        Boolean(materialTypeDocId) &&
                                        materialTypeDocId !== mainMaterialTypeDocId

                                    return (
                                        <MaterialRequirements
                                            key={field.key}
                                            namePath={[field.name]}
                                            prefix={['materialRequirements']}
                                            necessary={necessary}
                                            minMaterialWidth={
                                                isAdditionalMaterial
                                                    ? undefined
                                                    : minMainMaterialWidth
                                            }
                                            unrestrictedWidths={isAdditionalMaterial}
                                            initialDisabled={materialRequirementsHandled}
                                        />
                                    )
                                })}
                            </Space>
                        )}
                    </Form.List>

                    {materialRequirementsHandled && (
                        <Alert
                            showIcon
                            message={
                                'При сохранении новые резервы, заказы и задачи на разрезы будут созданы автоматически'
                            }
                            type='warning'
                        />
                    )}
                </Space>
            </Form>
        </Modal>
    )
}

export default MaterialRequirementsWrapper
